import type { LandingLocale } from '../../shared/content/landingContent.js';
import type { StaticPageKey } from '../../shared/content/staticPageContent.js';
import type { LandingEditorProject, LandingHomeSectionKey } from '../../shared/editor/landingEditorTypes.js';
import { resolveEditorNavigationTarget } from '../../shared/editor/landingEditorNavigation.js';
import {
  createNewLocalizationScope,
  isLocalizationScopeExit,
  type LocalizationEditorContext,
} from '../../shared/editor/landingEditorLocalizationFlow.js';
import { LandingEditorClient } from '../editor/landingEditorClient.js';
import { LandingEditorElementRegistry, getValueAtPath, setValueAtPath, type LandingEditorElementDefinition } from '../editor/landingEditorRegistry.js';
import type { InlineTextDraft } from '../editor/inlineTextCommit.js';
import {
  commitActiveVivaProseMirrorInlineEditor,
  readActiveVivaProseMirrorInlineResult,
  suppressNextVivaProseMirrorBlurCommit,
} from '../editor/vivaProseMirrorInlineEditor.js';
import { activeEditorPreviewPage } from './editorPreviewViewport.js';
import { renderEditorLocalizationFlow } from './editorLocalizationFlowView.js';

export const createEditorLocalizationFlowOperations = (context: Record<string, any>) => {
  const {
    activeEditorAudience, activeEditorPage, cloneEditorValue, defaultFrame, definitions, definitionById,
    expandedSections, fieldSourceForProject, getOrderedStaticSections, handleAuthorizationError,
    geometryOverlayRect, inlineTextDraft, landingAnchorSection, landingPageContentRepository, landingProjectContentRepository,
    locale, localizationFlow, normalizeEditableText, project, selectedElementId, setActiveEditorAudienceState,
    setActiveEditorPageState, setExpandedSections, setInlineTextDraft, setInlineTextGeometry,
    setLocaleState, setMessage, setSelectedElementIdState, setStatus, updateProject,
  } = context;

  const editorContext = (overrides: Partial<LocalizationEditorContext> = {}): LocalizationEditorContext => ({
    pageKey: activeEditorPage,
    audience: activeEditorAudience,
    locale,
    elementId: selectedElementId,
    ...overrides,
  });
  const definitionsForContext = (
    editorProject: LandingEditorProject,
    target: Pick<LocalizationEditorContext, 'pageKey' | 'audience' | 'locale'>,
  ) => {
    const landingContent = landingProjectContentRepository.landing(editorProject, target.locale);
    if (target.pageKey === 'landing') return LandingEditorElementRegistry.definitions(landingContent);
    const pageKey = target.pageKey as StaticPageKey;
    return LandingEditorElementRegistry.staticPageDefinitions(
      pageKey,
      landingProjectContentRepository.staticPage(editorProject, target.locale, target.audience, pageKey),
      landingContent,
      getOrderedStaticSections(editorProject, pageKey, target.audience),
    );
  };
  const definitionMap = (items: LandingEditorElementDefinition[]) => items.reduce<Record<string, LandingEditorElementDefinition>>((result, definition) => {
    result[definition.id] = definition;
    return result;
  }, {});
  const flushOnExit = (nextContext: LocalizationEditorContext, nextDefinitions = definitions) => {
    const nextDefinitionById = definitionMap(nextDefinitions);
    if (isLocalizationScopeExit(
      localizationFlow.dirtyScope,
      nextDefinitionById[nextContext.elementId],
      nextDefinitionById,
      nextContext,
    )) localizationFlow.leave(nextContext);
  };
  const commitOpenInlineText = () => {
    commitActiveVivaProseMirrorInlineEditor();
  };
  const scrollPreviewTarget = (anchorId?: string) => {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      const activePreview = activeEditorPreviewPage();
      if (!activePreview) return;
      if (!anchorId) return activePreview.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      const target = activePreview.querySelector<HTMLElement>(`#${window.CSS?.escape ? window.CSS.escape(anchorId) : anchorId}`);
      (target ?? activePreview).scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }, 80);
  };

  const setSelectedElementId = (elementId: string) => {
    if (localizationFlow.round) return;
    if (elementId !== selectedElementId) commitOpenInlineText();
    const nextContext = editorContext({ elementId });
    flushOnExit(nextContext);
    setSelectedElementIdState(elementId);
  };
  const setLocale = (nextLocale: LandingLocale) => {
    if (localizationFlow.round || nextLocale === locale) return;
    commitOpenInlineText();
    const nextBaseContext = editorContext({ locale: nextLocale });
    const nextDefinitions = definitionsForContext(project, nextBaseContext);
    const nextElementId = nextDefinitions.some(definition => definition.id === selectedElementId) ? selectedElementId : 'page.root';
    flushOnExit({ ...nextBaseContext, elementId: nextElementId }, nextDefinitions);
    setLocaleState(nextLocale);
    setSelectedElementIdState(nextElementId);
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
  };
  const switchEditorPage = (pageKey: LocalizationEditorContext['pageKey'], anchorId?: string) => {
    if (localizationFlow.round) return;
    commitOpenInlineText();
    const section = pageKey === 'landing' && anchorId ? landingAnchorSection[anchorId] : null;
    const targetElementId = section ? `section.${section}` : 'page.root';
    const nextBaseContext = editorContext({ pageKey });
    const nextDefinitions = definitionsForContext(project, nextBaseContext);
    const nextElementId = nextDefinitions.some(definition => definition.id === targetElementId) ? targetElementId : 'page.root';
    flushOnExit({ ...nextBaseContext, elementId: nextElementId }, nextDefinitions);
    setActiveEditorPageState(pageKey);
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    setSelectedElementIdState(pageKey === 'landing' ? nextElementId : 'page.root');
    scrollPreviewTarget(pageKey === 'landing' ? anchorId : undefined);
  };
  const handleEditorAudienceChange = (audience: LocalizationEditorContext['audience']) => {
    if (localizationFlow.round || audience === activeEditorAudience) return;
    commitOpenInlineText();
    const nextContext = editorContext({ audience, elementId: 'page.root' });
    flushOnExit(nextContext, definitionsForContext(project, nextContext));
    setActiveEditorAudienceState(audience);
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    setSelectedElementIdState('page.root');
    scrollPreviewTarget();
  };
  const handleEditorNavigate = (href: string) => {
    const target = resolveEditorNavigationTarget(href, window.location.origin);
    if (target.kind === 'page') switchEditorPage(target.pageKey, target.anchorId);
    if (target.kind === 'external') window.open(target.href, '_blank', 'noopener');
  };
  const handlePreviewLinkClick = (event: MouseEvent) => {
    if (event.defaultPrevented) return;
    const link = (event.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null;
    const href = link?.getAttribute('href');
    if (!href) return;
    event.preventDefault();
    event.stopPropagation();
    handleEditorNavigate(href);
  };
  const recordLocalizedFieldCommit = (definition: LandingEditorElementDefinition, previousValue: string, value: string) => {
    if (localizationFlow.round) return;
    const context = editorContext({ elementId: definition.id });
    localizationFlow.recordWysiwygTextExit(
      definition,
      definitionById,
      context,
      previousValue,
      value,
      context,
    );
  };
  const stageLocalizationTextResult = (result: { elementId: string; value: string; styles: NonNullable<InlineTextDraft['styles']> }) => {
    const step = localizationFlow.round?.steps[localizationFlow.round.activeIndex];
    if (!step || step.field.kind !== 'text' || step.field.elementId !== result.elementId) return false;
    const value = normalizeEditableText(result.value);
    localizationFlow.setCandidate(value, 'manual');
    setInlineTextDraft({
      elementId: result.elementId,
      value,
      styles: result.styles,
      editorKey: inlineTextDraft?.elementId === result.elementId ? inlineTextDraft.editorKey : step.id,
    });
    return true;
  };
  const markNewSectionForLocalization = (section: LandingHomeSectionKey, nextProject: LandingEditorProject) => {
    const nextDefinitions = definitionsForContext(nextProject, editorContext());
    const nextDefinitionById = definitionMap(nextDefinitions);
    const root = nextDefinitionById[`section.${section}`];
    if (!root) return;
    localizationFlow.markNew(createNewLocalizationScope(
      root, nextDefinitions, nextDefinitionById, editorContext({ elementId: root.id }),
      definition => getValueAtPath(fieldSourceForProject(nextProject, definition), definition.path),
    ));
  };
  const markNewRepeatForLocalization = (_definition: LandingEditorElementDefinition | undefined, repeatRootId: string, nextProject: LandingEditorProject) => {
    const nextDefinitions = definitionsForContext(nextProject, editorContext());
    const nextDefinitionById = definitionMap(nextDefinitions);
    let root = nextDefinitionById[repeatRootId];
    while (root?.parentId && root.repeatIndex === undefined) root = nextDefinitionById[root.parentId];
    if (!root) return;
    const stableRepeatRootId = root.repeatIndex === undefined ? repeatRootId : root.id;
    localizationFlow.markNew(createNewLocalizationScope(
      root, nextDefinitions, nextDefinitionById, editorContext({ elementId: stableRepeatRootId }),
      definition => getValueAtPath(fieldSourceForProject(nextProject, definition), definition.path), stableRepeatRootId,
    ));
  };

  const restoreContext = (target: LocalizationEditorContext, editorProject = project) => {
    setActiveEditorPageState(target.pageKey);
    setActiveEditorAudienceState(target.audience);
    setLocaleState(target.locale);
    const targetDefinitions = definitionsForContext(editorProject, target);
    setSelectedElementIdState(targetDefinitions.some(definition => definition.id === target.elementId) ? target.elementId : 'page.root');
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
  };
  const definitionForStep = (field: any, editorProject = project, stepLocale = locale) => {
    const scope = localizationFlow.round?.scope;
    if (!scope) return undefined;
    return definitionsForContext(editorProject, { pageKey: scope.pageKey, audience: scope.audience, locale: stepLocale })
      .find(definition => definition.id === field.elementId) ?? {
        id: field.elementId, label: field.label, kind: field.kind, path: field.path,
        source: field.source, staticPageKey: field.staticPageKey,
      } satisfies LandingEditorElementDefinition;
  };
  const prepareStep = async (round = localizationFlow.round, editorProject = project) => {
    if (!round || round.phase === 'saving' || round.phase === 'save-error') return;
    suppressNextVivaProseMirrorBlurCommit();
    const step = round.steps[round.activeIndex];
    if (!step) return;
    const scope = round.scope;
    const stepContext = { pageKey: scope.pageKey, audience: scope.audience, locale: step.locale, elementId: step.field.elementId };
    setActiveEditorPageState(scope.pageKey);
    setActiveEditorAudienceState(scope.audience);
    setLocaleState(step.locale);
    setSelectedElementIdState(step.field.elementId);
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    const stepDefinitions = definitionsForContext(editorProject, stepContext);
    const stepDefinitionById = definitionMap(stepDefinitions);
    const nextExpanded = { ...expandedSections };
    let cursor = stepDefinitionById[step.field.elementId];
    while (cursor?.parentId && cursor.parentId !== 'page.root') {
      nextExpanded[cursor.parentId] = true;
      cursor = stepDefinitionById[cursor.parentId];
    }
    setExpandedSections(nextExpanded);
    const definition = definitionForStep(step.field, editorProject, step.locale);
    if (!definition?.path) return localizationFlow.setError('Das registrierte Element konnte in dieser Sprache nicht gefunden werden.');
    const targetValue = getValueAtPath(fieldSourceForProject(editorProject, definition, step.locale, scope.audience), definition.path);
    if (step.field.kind !== 'text') {
      const candidate = step.field.kind === 'track' ? '' : step.field.currentValue;
      localizationFlow.prepare(candidate, targetValue, candidate && candidate === targetValue ? 'already-current' : 'source');
      if (step.field.kind === 'image' || step.field.kind === 'video' || step.field.kind === 'track') void LandingEditorClient.listMediaAssets()
        .then(assets => localizationFlow.setMediaOptions(assets
          .filter(asset => asset.kind === (step.field.kind === 'track' ? 'caption' : step.field.kind))
          .map(asset => ({ value: asset.url, label: asset.displayName }))))
        .catch(() => localizationFlow.setMediaOptions([]));
      return;
    }
    localizationFlow.prepare(
      targetValue,
      targetValue,
      targetValue === step.field.currentValue ? 'already-current' : 'manual',
    );
    setInlineTextDraft({
      elementId: step.field.elementId,
      value: targetValue,
      styles: editorProject.elementFrames[step.field.elementId]?.inlineTextStyles?.[step.locale] ?? [],
      editorKey: step.id,
    });
  };
  const saveRound = async (snapshot: LandingEditorProject) => {
    localizationFlow.setPhase('saving');
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    setStatus('saving');
    try {
      const target = await LandingEditorClient.saveDraft(snapshot);
      setStatus(target === 'backend' ? 'saved' : 'local');
      const round = localizationFlow.round;
      if (!round) return;
      const confirmed = round.steps.filter(step => step.status === 'confirmed').length;
      const skipped = round.steps.filter(step => step.status === 'skipped').length;
      setMessage(`${confirmed} Sprachinhalte übernommen, ${skipped} übersprungen. Draft ${target === 'backend' ? 'gespeichert' : 'lokal gesichert'}.`);
      localizationFlow.setPhase('complete');
      window.setTimeout(() => {
        const finished = localizationFlow.finish();
        if (finished) restoreContext(finished.scope.returnContext, snapshot);
      }, 900);
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      localizationFlow.setError(error instanceof Error ? error.message : 'Der Sprach-Draft konnte nicht gespeichert werden.');
      localizationFlow.setPhase('save-error');
    }
  };
  const startRound = () => {
    const round = localizationFlow.start(landingPageContentRepository.locales);
    if (round) void prepareStep(round);
  };
  const confirmStep = () => {
    const round = localizationFlow.round;
    const step = round?.steps[round.activeIndex];
    if (!round || !step || round.phase !== 'editing') return;
    const definition = definitionForStep(step.field, project, step.locale);
    if (!definition?.path) return localizationFlow.setError('Das Ziel-Feld ist nicht mehr registriert.');
    const activeText = step.field.kind === 'text' ? readActiveVivaProseMirrorInlineResult() : null;
    const value = step.field.kind === 'text' ? normalizeEditableText(activeText?.value ?? round.candidate) : round.candidate;
    const nextProject = cloneEditorValue(project);
    setValueAtPath(fieldSourceForProject(nextProject, definition, step.locale, round.scope.audience), definition.path, value);
    if (step.field.kind === 'text') {
      const frame = nextProject.elementFrames[step.field.elementId] ?? defaultFrame();
      const inlineTextStyles = { ...frame.inlineTextStyles };
      const inlineTextSources = { ...frame.inlineTextSources };
      const styles = activeText?.styles ?? inlineTextDraft?.styles ?? [];
      if (styles.length) { inlineTextStyles[step.locale] = styles; inlineTextSources[step.locale] = value; }
      else { delete inlineTextStyles[step.locale]; delete inlineTextSources[step.locale]; }
      nextProject.elementFrames[step.field.elementId] = {
        ...frame,
        inlineTextStyles: Object.keys(inlineTextStyles).length ? inlineTextStyles : undefined,
        inlineTextSources: Object.keys(inlineTextSources).length ? inlineTextSources : undefined,
      };
    }
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    updateProject(nextProject, 'unsaved', { mergeInlineDraft: false });
    const nextRound = localizationFlow.settle('confirmed');
    if (nextRound?.phase === 'saving') void saveRound(nextProject);
    else window.setTimeout(() => void prepareStep(nextRound, nextProject), 0);
  };
  const advanceWithoutChange = (skipLocale = false) => {
    suppressNextVivaProseMirrorBlurCommit();
    const nextRound = skipLocale ? localizationFlow.skipLocale() : localizationFlow.settle('skipped');
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    if (nextRound?.phase === 'saving') void saveRound(project);
    else window.setTimeout(() => void prepareStep(nextRound), 0);
  };
  const exitRound = () => {
    suppressNextVivaProseMirrorBlurCommit();
    const finished = localizationFlow.finish();
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    if (finished) restoreContext(finished.scope.returnContext);
  };
  const previousStep = () => {
    suppressNextVivaProseMirrorBlurCommit();
    const round = localizationFlow.previous();
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
    if (round) void prepareStep(round);
  };
  const renderLocalizationFlow = () => renderEditorLocalizationFlow({
    flow: localizationFlow,
    localeOrder: landingPageContentRepository.locales,
    highlightRect: geometryOverlayRect,
    onDismissPrompt: () => localizationFlow.dismissPrompt(),
    onStart: startRound,
    onCandidateChange: value => localizationFlow.setCandidate(value, 'manual'),
    onMediaUpload: async (event: Event) => {
      const input = event.currentTarget as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      try {
        const upload = await LandingEditorClient.uploadAsset(file);
        localizationFlow.setCandidate(upload.url, 'manual');
      } catch (error) {
        if (!handleAuthorizationError(error)) localizationFlow.setError(error instanceof Error ? error.message : 'Upload fehlgeschlagen.');
      } finally { input.value = ''; }
    },
    onRetry: () => void prepareStep(localizationFlow.round, project),
    onPrevious: previousStep,
    onSkip: () => advanceWithoutChange(false),
    onSkipLocale: () => advanceWithoutChange(true),
    onConfirm: confirmStep,
    onExit: exitRound,
    onRetrySave: () => void saveRound(project),
  });

  return {
    definitionMap,
    definitionsForContext,
    editorContext,
    handleEditorAudienceChange,
    handleEditorNavigate,
    handlePreviewLinkClick,
    markNewRepeatForLocalization,
    markNewSectionForLocalization,
    recordLocalizedFieldCommit,
    renderLocalizationFlow,
    setLocale,
    setSelectedElementId,
    stageLocalizationTextResult,
    switchEditorPage,
  };
};
